import { z } from 'zod';

import { Button } from '@/shared/ui';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { useAppForm } from '@/shared/ui/form';

const reportFolderSchema = z.object({
  name: z.string().trim().min(1, 'Folder name is required'),
});

type Props = {
  mode: 'create' | 'rename';
  defaultName?: string;
  isOpen: boolean;
  close: () => void;
  unmount: () => void;
  onSubmit: (name: string) => Promise<void>;
};

export function ReportFolderFormDialog({
  mode,
  defaultName = '',
  isOpen,
  close,
  unmount,
  onSubmit,
}: Props) {
  const isCreate = mode === 'create';
  const form = useAppForm({
    defaultValues: { name: defaultName },
    validators: { onChange: reportFolderSchema },
    onSubmit: async ({ value }) => {
      await onSubmit(value.name.trim());
      close();
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={() => close()} onExitComplete={unmount}>
      <DialogContent size="s">
        <DialogHeader>
          <DialogTitle>{isCreate ? 'Create folder' : 'Rename folder'}</DialogTitle>
          <DialogDescription>
            {isCreate
              ? 'Create a folder to organize your reports.'
              : 'Enter a new name for this folder.'}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="pb-2">
          <form.AppField name="name">
            {(field) => (
              <div className="flex flex-col gap-2">
                <label
                  className="text-sm font-medium text-foreground"
                  htmlFor={field.name}
                >
                  Folder name
                </label>
                <field.TextField autoFocus maxLength={80} />
              </div>
            )}
          </form.AppField>
        </DialogBody>
        <DialogFooter>
          <div />
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={close}>
              Cancel
            </Button>
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
            >
              {([canSubmit, isSubmitting]) => (
                <Button
                  type="button"
                  disabled={!canSubmit || isSubmitting}
                  isLoading={isSubmitting}
                  onClick={form.handleSubmit}
                >
                  {isCreate ? 'Create' : 'Rename'}
                </Button>
              )}
            </form.Subscribe>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
